import { forwardRef, useMemo } from 'react';
import styled from '@emotion/styled';
import type { RootNode } from '@trinity/chat/types/rich-text';
import { BasicRichText } from '../BasicRichText/BasicRichText';

type Props = {
  title: string;
  backgroundColor: string;
  values: string[];
  labels: string[];
  insights?: string;
};

export const KPIWidget = forwardRef<HTMLDivElement, Props>(({
  title,
  backgroundColor,
  values,
  labels,
  insights,
}, ref) => {
  const getGridLayout = (count: number): string => {
    switch (count) {
      case 1:
        return '1fr';
      case 2:
      case 3:
        return `repeat(${count}, 1fr)`;
      case 4:
        return 'repeat(2, 1fr)';
      case 5:
        return 'repeat(3, 1fr)';
      case 6:
        return 'repeat(3, 1fr)';
      default:
        return '1fr';
    }
  };

  const getGridRows = (count: number): string => {
    switch (count) {
      case 1:
      case 2:
      case 3:
        return '1fr';
      case 4:
      case 6:
        return 'repeat(2, 1fr)';
      case 5:
        return 'auto auto';
      default:
        return '1fr';
    }
  };

  const validValues = values.slice(0, 6); // Ensure max 6 values
  const validLabels = labels.slice(0, 6); // Ensure max 6 labels

  // Memoized insights parsing - only recalculates when insights prop changes
  const renderedInsights = useMemo(() => {
    if (!insights) return null;

    try {
      const parsedInsights = JSON.parse(insights) as RootNode;
      return <BasicRichText value={parsedInsights} />;
    } catch {
      // If parsing fails, treat as plain text
      return <InsightsPlainText>{insights}</InsightsPlainText>;
    }
  }, [insights]);

  return (
    <KPIContainer ref={ref} backgroundColor={backgroundColor}>
      <KPIHeader>
        <KPITitle>{title}</KPITitle>
      </KPIHeader>

      <KPIValuesGrid
        count={validValues.length}
        gridColumns={getGridLayout(validValues.length)}
        gridRows={getGridRows(validValues.length)}>
        {validValues.map((value, index) => {
          const label = validLabels[index] || `KPI ${index + 1}`;

          // Handle special layout for 5 values - first 3 in first row, last 2 in second row
          const gridColumn = validValues.length === 5 && index >= 3
            ? `${index - 2} / ${index - 1}`
            : undefined;

          return (
            <KPIValueItem key={index} gridColumn={gridColumn}>
              <KPIValueNumber style={{ fontSize: (validValues.length === 1 ? '2.6rem' : '2rem') }}>{value}</KPIValueNumber>
              <KPIValueLabel>{label}</KPIValueLabel>
            </KPIValueItem>
          );
        })}
      </KPIValuesGrid>

      {insights && <KPIInsights>{renderedInsights}</KPIInsights>}
    </KPIContainer>
  );
});

KPIWidget.displayName = 'KPIWidget';

const KPIContainer = styled.div<{ backgroundColor: string }>(({ backgroundColor, theme }) => ({
  backgroundColor: backgroundColor || '#ffffff',
  color: theme.palette.text.primary,
  borderRadius: '18px',
  padding: '20px',
  maxWidth: '450px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

const KPIHeader = styled.div({
  textAlign: 'left',
});

const KPITitle = styled.h3(({ theme }) => ({
  margin: 0,
  fontSize: '1.5rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

const KPIValuesGrid = styled.div<{
  count: number;
  gridColumns: string;
  gridRows: string;
}>(({ count, gridColumns, gridRows }) => ({
  display: 'grid',
  gridTemplateColumns: gridColumns,
  gridTemplateRows: gridRows,
  gap: '16px',
  flex: 1,
  alignItems: 'center',
  justifyItems: 'center',
  ...(count === 1 && {
    justifyContent: 'center',
    alignContent: 'center',
  }),
}));

const KPIValueItem = styled.div<{ gridColumn?: string }>(({ gridColumn }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minHeight: '80px',
  width: '100%',
  ...(gridColumn && {
    gridColumn,
  }),
}));

const KPIValueNumber = styled.div(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: '4px',
  lineHeight: 1.2,
}));

const KPIValueLabel = styled.div(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.primary,
  fontWeight: 500,
  textAlign: 'center',
  wordBreak: 'break-word',
  textWrap: 'balance',
}));

const KPIInsights = styled.div();

const InsightsPlainText = styled.div(({ theme }) => ({
  fontSize: '0.875rem',
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  whiteSpace: 'pre-wrap',
}));